import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import './overridden-placeholder.css'
import RGL, {WidthProvider} from "react-grid-layout";
import {useContext, useState, useEffect, useRef} from "react";
import {FormGroup, FormControlLabel, Checkbox, Button} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import {makeStyles} from "@material-ui/core/styles";
import useAxios from "axios-hooks";
import axios from 'axios';
import {AuthContext, ThemeContext} from "../../contexts";
import {
    Box,
    Backdrop,
    Typography,
    TextField,
    Chip
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import _ from 'lodash'
import Moment from "moment";
import ContainerDimensions from "react-container-dimensions";
import { Charts, ChartContainer, ChartRow, LabelAxis, ValueAxis, LineChart, Baseline, Legend, styler, YAxis } from "react-timeseries-charts";
import { TimeSeries, TimeRange, avg, stdev} from "pondjs";
import { format } from "d3-format";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import MonthPicker from "../create_new_relationship/rules_components/MonthPicker";
import { Autocomplete } from '@material-ui/lab';
import CustomLoader from "../../components/CustomLoader";

const devicesConf = {
    "ename:Ossigeno_disciolto":{
        "key": "gs1:MassConcentration",
        "label": "Ossigeno disciolto",
        "udm": "mg/L",
        "min": 6,
        "max": 12,
        "deviceFile": "ossigeno",
        "line":{
            "id":"ossig"
        }
    },
    "ename:Temperatura":{
        "key": "gs1:Temperature",
        "label": "Temperatura",
        "udm": "°C",
        "min": 13,
        "max": 30,
        "deviceFile": "temperatura",
        "line":{
            "id":"temp"
        }
    },
    "ename:Ph":{
        "key": "gs1:Ph",
        "label": "pH",
        "udm": "pH",
        "min": 7.5,
        "max": 8.5,
        "deviceFile": "ph",
        "line":{
            "id":"ph"
        }
    },
    "ename:Potenziale_ossidoriduzione":{
        "key": "gs1:Voltage",
        "label": "Potenziale ossidoriduzione",
        "udm": "mV",
        "min": 240,
        "max": 280,
        "deviceFile": "pot_ossidorid",
        "line":{
            "id":"pot_oss"
        }
    },
    "ename:Torbidita":{
        "key": "gs1:Torbidity",
        "label": "Torbidità",
        "udm": "NTU",
        "min": 6,
        "max": 50,
        "deviceFile": "torbidita",
        "line":{
            "id":"torb"
        }
    },
    "ename:Conduttivita_elettrica":{
        "key": "gs1:Conductivity",
        "label": "Conduttività elettrica",
        "udm": "µS/cm",
        "min": 50000,
        "max": 60000,
        "deviceFile": "condut_elettrica",
        "line":{
            "id":"c_elettr"
        }
    }
}

const locationColor = {
    "http://main.climavibe.com.ichain.pro/codes/loc/1.0": "#ff0000ff",
    "http://main.climavibe.com.ichain.pro/codes/loc/2.0": "#0037ffff",
    "http://main.climavibe.com.ichain.pro/codes/loc/3.0": "#22c01aff"
}
const legendStyle = styler(
    [
        { key: "http://main.climavibe.com.ichain.pro/codes/loc/1.0", color: "#ff0000ff", width: 2 },
        { key: "http://main.climavibe.com.ichain.pro/codes/loc/2.0", color: "#0037ffff", width: 2 },
        { key: "http://main.climavibe.com.ichain.pro/codes/loc/3.0", cloro: "#22c01aff", width: 2 }
    ]
);

const MeasureEnvChart = ({t, sglns, device, width, height, filter, backgroundColor, handleTrackerChanged, 
    handleTimeRangeChange, handleMouseMove, tracker, timerange, totalTimerange, domain,
    cachedSeries, setCachedSeries}) => {
    
    const [{minStat, maxStat}, setStats] = useState({minStat:0, maxStat:0});
    const [{series, ready}, setSeries] = useState({ready:false})

    useEffect( () => {
        const fetchSensorData = async() => {
            try {
                const allRequests = []
                const locationRequest = sglns?.data?.map( (l) => 
                    axios.get(`/api/dataservices/sensor_data/`,{
                        params: {
                            where: l.sgln,
                            type_of_report: device._id,
                            start: Moment(totalTimerange.begin()).format('YYYY-MM-DD HH:mm:ss'),
                            end: Moment(totalTimerange.end()).format('YYYY-MM-DD HH:mm:ss'),
                            domain: domain
                        }
                    }).then((response) => ({
                        device: device,
                        location: l,
                        data: response.data
                    }))
                )
                allRequests.push(...locationRequest)
                
                const results = await Promise.all(allRequests);
                const series = new Map();
                results?.map((r) => {
                    const column = devicesConf[r.device._id].key
                    const points = r.data?.records?.map((row) => [new Date(row["timestamp"]).getTime(), parseFloat(row[column])])
                    points.sort((a,b)=> a[0]-b[0])
                    const ts = {
                        name: devicesConf[r.device._id].label,
                        columns: ["time", "value"],
                        points: points
                    }
                    if (ts.points.length > 0 )
                        series.set(r.location.sgln, new TimeSeries(ts))
                })
                setSeries({series: series, ready:true});
                setCachedSeries((prev)=>prev.set(device._id, series))
            }
            catch (err) {console.log(err)}
        }
        if(cachedSeries){
            setSeries({series: cachedSeries, ready: true})
        }
        else {
            fetchSensorData();
        }
    }
    ,[device, sglns, totalTimerange]);
    
    useEffect( ()=>{
        let newStats={minStat:{}, maxStat:{}}
        
        let activeSeries= series?.entries();
        if (activeSeries){
            let filteredSeries = activeSeries.filter(([sgln, timeseries])=> 
                filter.where.find((v)=> 
                    v.key===sgln).active);
            if (filteredSeries){
                let croppedSeries = Array.from(filteredSeries.map((z)=>z[1].crop(timerange)));
                newStats.minStat = Math.min(devicesConf[device._id].min, ...croppedSeries.map((tmin)=>tmin.min()!==undefined?tmin.min():devicesConf[device._id].min));
                newStats.maxStat = Math.max(devicesConf[device._id].max, ...croppedSeries.map((tmax)=>tmax.max()!==undefined?tmax.max():devicesConf[device._id].max));
            }
            setStats({...newStats, statsReady:true})
        }
    }, [timerange, filter, series])
    
    const trackerInfoValues = () => (
        filter.where.filter(({key, active}) => active && series.get(key)).map(({key, active}) => {
        if (series){
            const fmt = format(",.2f");

            let aSeries = series.get(key).crop(timerange);

            let v = "--";
            if (tracker && series) {
                const i = aSeries.bisect(new Date(tracker));
                const vv = aSeries.at(i)?.get("value");
                if (vv!==undefined) {
                    v = fmt(vv);
                }
            }

                const label = sglns?.data?.find(s=>s.sgln===key).name;
                const value = `${v} ${devicesConf[device._id].udm}`;

                return { label, value };
            }
        })
    );

    return (ready&&<div key={`${device._id}_contdiv`} style={{borderStyle:"solid", borderWidth:1, padding:0, borderColor:backgroundColor, height:height*0.9}}>
        <ChartContainer
            transition={300}
            key={device._id}
            showGrid={true}
            enablePanZoom={true}
            width={width-48}
            //padding={10}
            onTrackerChanged={handleTrackerChanged}
            onTimeRangeChanged={handleTimeRangeChange}
            onMouseMove={(x, y) => handleMouseMove(x, y)}
            trackerPosition={tracker}
            minDuration={1000 * 60 * 60 * 24}
            timeRange={timerange}
            
        >
            <ChartRow key={`c_row_${device._id}`} 
                height={height*0.9 - 30}
                axisMargin={-10} 
                trackerShowTime={true}
                trackerInfoValues={trackerInfoValues(device._id)}
                trackerInfoHeight={10 + trackerInfoValues(device._id).length * 16}
                trackerInfoWidth={250}
            >
                <LabelAxis id={`${device._id}_axis`}
                    label={t(`climavibeEnvChart.graph.titles.${devicesConf[device._id].line.id}`)}
                    values={trackerInfoValues(device._id)}
                    hideScale={true}
                    min={minStat - ((maxStat-minStat)*0.05)}
                    max={maxStat + ((maxStat-minStat)*0.05)}
                    width={250}
                    //valWidth={240}
                    //labelOffset={-150}
                    type="linear"
                    format=",.2f"
                />
                
                <Charts>
                    {filter.where
                    .filter(({key,active}) => series.get(key))
                    .map(({key, active}) =>
                        (<LineChart key={`my_line_${device._id}_${key}`}
                            axis={`${device._id}_axis`}
                            visible={active}
                            style={{ 
                                value:{
                                    normal: { stroke: locationColor[key], fill: "none", strokeWidth: 1 },
                                    highlighted: { stroke: "#000000", fill: "none", strokeWidth: 1 },
                                    selected: { stroke: "#000000", fill: "none", strokeWidth: 2 },
                                    muted: { stroke: "#000000", fill: "none", opacity: 0.4, strokeWidth: 1 }
                                }
                            }}
                            breakLine={true}
                            series={series.get(key)}
                            /> )
                    )}
                    <Baseline
                        axis={`${device._id}_axis`}
                        value={devicesConf[device._id].max}
                        label={t('climavibeEnvChart.graph.optimal_max')}
                        position="right"
                    />
                    <Baseline
                        axis={`${device._id}_axis`}
                        value={devicesConf[device._id].min}
                        label={t('climavibeEnvChart.graph.optimal_min')}
                        position="right"
                    />
                </Charts>
                <YAxis id={`${device._id}_axis_y`} type="linear"
                    width={50}
                    min={minStat - ((maxStat-minStat)*0.05)}
                    max={maxStat + ((maxStat-minStat)*0.05)}
                />
                <ValueAxis id={`${device._id}_axis_value`}
                    detail={devicesConf[device._id].udm}
                    values={trackerInfoValues(device._id)}
                    width={50}
                    min={minStat-((maxStat-minStat)*0.05)}
                    max={maxStat+((maxStat-minStat)*0.05)}
                />
            </ChartRow>
        </ChartContainer>
    </div>)
}

const ClimavibeEnvChart =  () => {
    const ReactGridLayout = WidthProvider(RGL);
    const authContext = useContext(AuthContext);
    const {primaryColor, secondaryColor, backgroundColor, accentColorAlpha, accentColor } = useContext(ThemeContext)
    const useStyles = makeStyles((theme) => ({
        root: {
            flexGrow: 1,
            '& label.Mui-focused': {
                color: 'black',
            },
            '& .MuiInput-underline:after': {
                borderBottomColor: accentColor,
            },
            '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                    borderColor: accentColor,
                },
            },
            '& .MuiIconButton-colorInherit': {
                color: accentColor
            },
            '& ..MuiAutocomplete-inputRoot': {
                maxHeigth: '30px'
            }
        },
        chip: {
            marginRight: '1%',
            color: accentColor,
            border: '1px solid ' + accentColor,
            fontWeight: 'bold',
            backgroundColor: 'transparent'
        },
        button: {
            backgroundColor: accentColor,
            color: "white",
            fontWeight: 'bold',
            marginLeft: '2%',
            borderRadius: '20px',
        },
        paper: {
            paddingTop: '1%',
            paddingLeft:'2%',
            paddingBottom: '5%',
            paddingRight: '2%',
            fontSize: 'small'
        },
        graph_tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 'thin',
            borderRadius: '5px',
            border: '1px solid ',
            padding: '0.25em',
            //minWidth: '310px'            
        },
        graph_tooltip_inner: {
            marginLeft: '1em',
            marginRight: '1em',
            whiteSpace: 'nowrap'
        },
        dot: {
            height: '20px',
            width: '20px',
            borderRadius: '50%',
            display: 'inline-block',
        },
        box: {
            width: '100%',
            //height: '100%',
            padding: '8px',
        },
        box2: {
            width: '100%',
            height: '100%',
            padding: '8px',
            overflow: 'hidden'
        },
        boxBar: {
            width: '100%',
            height: '100%',
            padding: '8px',
        },
        boxLeft: {
            height: '100%',
            width: '18%',
            margin: '0px',
            float: 'left',
        },
        boxInsideLeft: {
            height: '100%',
            position: 'relative',
        },
        gridLayoutLeft: {
            backgroundColor: backgroundColor,
            top: 0, left: 0, right: 0,
            margin: 'auto',
            borderRadius: '8px',
            height: '100%'
        },
        boxRight: {
            height: '100%',
            width: '80%',
            margin: '0px',
            float: 'right',
        },
        editBox: {
            position: 'absolute',
            top: '0',
            right: '0',
        },
        titleBox: {
            position: 'absolute',
            top: '0',
            left: '0',
            height: '100%',
            display: 'flex',
            alignItems: 'center'
        },
        titleBar: {
            fontWeight: "bolder",
            fontSize: '0.85rem'
        },
        iconEdit: {
            width: '0.75em',
            height: '0.75em',
        }
    }));
    const classes = useStyles()

    const { t } = useTranslation();
    
    const defaultProps = {
        className: "env_analysis_layout",
        cols: 12,
        rowHeight: 100,
        onLayoutChange: ()=>{}
    };

    function onLayoutChange(layout, name) {
        saveToLS(name, layout);
    }

    function getFromLS(key) {
        if (localStorage) {
            return JSON.parse(localStorage.getItem(`${authContext.userName}_${authContext.companyPrefix}_${key}`)) || []
        }
    }

    function saveToLS(key, value) {
        if (localStorage) {
            localStorage.setItem(`${authContext.userName}_${authContext.companyPrefix}_${key}`, JSON.stringify(value));
        }
    }

    const originalLayout = getFromLS("env_analysis_layout");
    const [state,] = useState(originalLayout)

    const minDate= Moment("2025-07-01 00:00");

     
    const [cachedSeries, setCachedSeries] = useState(new Map());

    const [filter, setFilter] = useState({
        where: [],
        selectedDevices: {},
        nOfSelectedDevices: 0,
        currentMonthDate: Moment().toDate(),
        ready: false
    })
    
    const [totalTimerange, setTotalTimerange]= useState(new TimeRange(new Date(filter.currentMonthDate.getFullYear(), filter.currentMonthDate.getMonth(), 1), 
        new Date(filter.currentMonthDate.getFullYear(), filter.currentMonthDate.getMonth()+1,0)));

    const [{ data: sglns, loading:loadingSglns, error: errorSglns },] = useAxios({
        url: `/api/subplaces/?mine=true&not_mine=false&skip=0&limit=100&direction=1&domain=${authContext.activeMainDomain}`,
        useCache: false
    },
    )

    const [{ data: devices, loading:loadingDevices, error:errorDevices },] = useAxios({
        url: `/api/dataservices/records/_keys/?key_type=ename&domain=${authContext.activeMainDomain}`,
        useCache: false
    },
    )

    /* Converte un array di oggetti in CSV (con header) */
    function toCSV(ts) {
        //const sep = ";"
        const giorni= new Map();
        //const headers = `data${sep}ora${sep}valore${sep}Media giornaliera${sep}Dev. Std\n`;
        const dailyStats = ts.dailyRollup({aggregation:{
            media: {
                value: avg()
            },
            deviazione: {
                value: stdev()
            }
        }
        });
        const rows = [...ts.events()].map((point) => {
            const e_date = new Date(point.timestamp());
            const day = Moment(point.timestamp()).startOf("day").toDate();
            let m = null;
            let std = null;
            if (!giorni.has(day.toISOString())){
                m = dailyStats.atTime(day).get("media");
                std = dailyStats.atTime(day).get("deviazione");
                giorni.set(day.toISOString(),true);
            }
            return {
                "Data": e_date.toLocaleDateString(),
                "Ora": e_date.toLocaleTimeString(),
                "Valore": point.get(),
                "Media Giornaliera": m,
                "Dev. Std.": std
            }
        });
        // Crea worksheet e workbook
        const worksheet = XLSX.utils.json_to_sheet(rows, {cellDates:true});

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "TimeSeries");

        // Genera l’Excel come array buffer
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        return excelBuffer
        //return "\uFEFF" + headers + [...rows].join("\n");
    };

    /** Scarica una singola serie come CSV */
    function downloadOneCSV(ts) {
        const csv = toCSV(ts);
        return csv;
    }

  // Scarica tutti i file uno dopo l’altro con una piccola pausa
    const downloadAll = async () => {
        const coeff = 0.00064;
        const zip = new JSZip();
        cachedSeries.forEach( (deviceMap, device) => {
            if (filter.selectedDevices[device]){
                const deviceFile = devicesConf[device].deviceFile
                deviceMap.forEach( (ts, loc) => {
                    if (filter.where.filter(({key, active})=> key===loc && active).length > 0){
                        const locFile= sglns.data.find(s=>s.sgln===loc).name.slice(-1)
                        zip.file(`${deviceFile}_${locFile}.xlsx`, downloadOneCSV(ts));
                        if (device === "ename:Conduttivita_elettrica"){
                            const eventi_salinita = [...ts.events().map( e => [e.timestamp(), e.get() * coeff])];
                            const es_ts = new TimeSeries({
                                name: "Salinità",
                                columns: ["time", "value"],
                                points: eventi_salinita
                            })
                            zip.file(`salinita_${locFile}.xlsx`, downloadOneCSV(es_ts))
                        }
                    }
                })
            }
        })
        const content = await zip.generateAsync({type: "blob"});
        saveAs(content, "dati_laguna.zip")
    };

    useEffect(()=>{
        if (!(loadingDevices || loadingSglns)){
            const newSelectedDevices = {}
            devices?.forEach(v=> newSelectedDevices[v._id]=true)
            const nOfSelectedDevices= Object.values(newSelectedDevices).filter(value => value===true).length
            setFilter((prev)=>({...prev,
                where: sglns?.data.map(s=>({ key: s.sgln, active: true})), 
                selectedDevices:newSelectedDevices, 
                nOfSelectedDevices: nOfSelectedDevices, 
                ready:true})); 
        }
    },[sglns, devices]);

    useEffect(()=>{
        const startDate = new Date(filter.currentMonthDate.getFullYear(), filter.currentMonthDate.getMonth(), 1)
        const endDate = new Date(filter.currentMonthDate.getFullYear(), filter.currentMonthDate.getMonth()+1,0)
        setTotalTimerange(new TimeRange(startDate,endDate));
        setCachedSeries(new Map());
        setTracker((prev)=>({...prev, timerange: new TimeRange(startDate, endDate)}))
    },[filter.currentMonthDate])

    const containerRef = useRef();

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const handler = (e) => {
        e.preventDefault();
        // lascia che ChartContainer gestisca lo zoom
        };
        el.addEventListener('wheel', handler, { passive: false });

        return () => {
        el.removeEventListener('wheel', handler, { passive: false });
        };
    }, []);

    const switchActiveLocations = (sgln) => {
        const actualWhere = filter.where;
        actualWhere.find((v)=> v.key===sgln).active= !(actualWhere.find((v)=> v.key===sgln).active)
        setFilter((prev)=> ({...prev, where: actualWhere}))
    }

    const [{tracker, timerange, x, y}, setTracker] = useState({
        tracker: null,
        timerange: new TimeRange(totalTimerange),
        x: null,
        y: null
    })

    const handleTrackerChanged =(tracker) => {
        if (!tracker){
            setTracker((prevTracker)=>({...prevTracker, tracker:tracker, x:null, y:null}));
        }
        else{
            setTracker((prevTracker)=>({...prevTracker, tracker:tracker}));
        }
    }
    const handleTimeRangeChange = newTimerange => {
        setTracker((prevTracker) => ({...prevTracker, timerange: totalTimerange.intersection(newTimerange) }));
        
    };

    const handleMouseMove = (x,y) => {
        setTracker((prevTracker) => ({...prevTracker, x: x, y: y}));
    }


    return (
        <div className={classes.root} id="env_charts_page" ref={containerRef} style={{ width:"100%", height:"88vh" }}>
        <ContainerDimensions >
        {({ width, height }) =>(
                <div style={{ height: height, width: width-48}}>
                    <Box mb="1em" display={"flex"} alignItems={"end"} >
                        
                        {!loadingDevices &&
                        <>
                            <FilterListIcon style={{ color: accentColor, marginRight: 16 }} />
                            <Autocomplete
                            style={{ width: '40%', marginRight: 16}}
                            size={"small"}
                            multiple={true}
                            limitTags={3}
                            id={`device_fcl`}
                            options={devices?.map(x=>x._id)}
                            getOptionLabel={(option)=>(t(`climavibeEnvChart.graph.titles.${devicesConf[option].line.id}`))}
                            getOptionSelected={(option,value)=> option === value}
                            value={Object.entries(filter.selectedDevices).filter(([key,active])=>active).map(([key,active])=>(key))}
                            onChange={(event, newSelection)=>{
                                const newSelectedDevices={}
                                devices?.forEach(d=>{
                                    newSelectedDevices[d._id]=newSelection.find((ns)=>(ns === d._id))?true:false
                                })
                                setFilter(prev=>({...prev, selectedDevices: newSelectedDevices, nOfSelectedDevices:newSelection.length}))
                            }}
                            renderInput={params => {
                                const { InputProps, ...restParams } = params;
                                const { startAdornment, ...restInputProps } = InputProps;
                                return (
                                    <TextField
                                        {...restParams}
                                        size={'small'}
                                        variant={"standard"}
                                        label={t('actions.filterBy', { by: t('climavibeEnvChart.filterBy.sensor') })}
                                        multiline={false}
                                        InputProps={{
                                            ...restInputProps,
                                            startAdornment: (
                                                <div style={{
                                                    maxHeight: '100px',
                                                    overflowY: 'auto',
                                                }}
                                                >
                                                    {startAdornment}
                                                </div>
                                            ),
                                        }}
                                        />
                                    );
                                }}
                            
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => (
                                    <Chip label={(t(`climavibeEnvChart.graph.titles.${devicesConf[option].line.id}`))} size='small' {...getTagProps({ index })} />
                                ))
                            }
                        />
                        </>
                        }
                        {!loadingSglns &&
                        <>
                            <FilterListIcon style={{ color: accentColor, marginRight: 16 }} />
                             <Autocomplete
                                style={{ width: '40%', marginRight: 16 }}
                                size="small"
                                multiple={true}
                                limitTags={3}
                                id={`location_fcl`}
                                options={_.uniqBy(sglns.data, '_id')}
                                getOptionLabel={(option)=>{
                                    return option.name;
                                }
                                }
                                getOptionSelected={(option,value)=> option._id === value._id}
                                value={sglns.data.filter((sgln)=>filter.where.find(s=>s.key===sgln.sgln)?.active )}
                                onChange={(event, newSelection)=>{
                                    const newWhere=sglns.data.map((s)=> ({key: s.sgln, active: newSelection.includes(s)}))
                                    setFilter(prev=>({...prev, where: newWhere}))
                                }}
                                renderInput={params => {
                                    //const { InputProps, ...restParams } = params;
                                    //const { startAdornment, ...restInputProps } = InputProps;
                                    return (
                                        <TextField
                                            {...params}
                                            size={'small'}
                                            variant={"standard"}
                                            label={t('actions.filterBy',{by:t('climavibeEnvChart.filterBy.monitoring_point')})}
                                            //placeholder={t('actions.filterBy', { by: t('climavibeEnvChart.filterBy.monitoring_point') })}
                                            //defaultValue="Pollo Piccolo"
                                            /*InputProps={{
                                                ...restInputProps,
                                                startAdornment: (
                                                    <div style={{
                                                        maxHeight: '100px',
                                                        overflowY: 'auto',
                                                    }}
                                                    >
                                                        {startAdornment}
                                                    </div>
                                                ),
                                            }}*/
                                            />
                                        );
                                    }}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip icon={<FiberManualRecordIcon style={{color: locationColor[option.sgln]}} />} label={option.name} size='small' {...getTagProps({ index })} />
                                    ))
                                }
                            />
                            {/*<Legend width={"40%"}
                                align="left"
                                style={styler(sglns?.data.map((s)=>({key:s.sgln, color: locationColor[s.sgln]})))}
                                categories={sglns?.data.map((s)=>({key:s.sgln, label:s.name}))}
                                onSelectionChange={switchActiveLocations}
                                highlight={"http://main.climavibe.com.ichain.pro/codes/loc/1.0"}
                            />*/}
                        </>
                        }
                        <MonthPicker 
                            name={"choose_month"} 
                            minDate={minDate} 
                            state={filter.currentMonthDate} 
                            setState={(newMonthDate)=> setFilter((prev)=>({...prev, currentMonthDate: newMonthDate}))}
                            label={t('actions.filterBy', {by: t('climavibeEnvChart.filterBy.month')})}/>
                            
                        <Button variant="outlined" color="primary" onClick={()=>downloadAll()}>{t('actions.download')}</Button>
                    </Box>
                {(loadingSglns || loadingDevices || !filter.ready) &&
                    <Backdrop style={{ zIndex: 1000 }} open={loadingSglns || loadingDevices || !filter.ready}><CustomLoader size={80} /></Backdrop>}
                {(!loadingSglns && !loadingDevices && filter.ready) &&
                    
                        <div style={{display: "flex", flexDirection:"column", width: width-48, height: height,
                            //borderStyle: "solid",
                            //borderWidth: 2,
                            //borderColor: primaryColor,
                            }}
                        >
                        {devices.filter((device)=> filter.selectedDevices[device._id]).map((device)=>
                            (
                                <MeasureEnvChart key={`measure_chart_${device._id}`} t={t}
                                    width={width}
                                    height={height/filter.nOfSelectedDevices}
                                    sglns={sglns}
                                    device={device}
                                    filter={filter}
                                    backgroundColor={backgroundColor}
                                    handleTrackerChanged={handleTrackerChanged}
                                    handleTimeRangeChange={handleTimeRangeChange}
                                    handleMouseMove={handleMouseMove}
                                    tracker={tracker}
                                    timerange={timerange}
                                    totalTimerange={totalTimerange}
                                    domain={authContext.activeMainDomain}
                                    cachedSeries={cachedSeries.get(device._id)}
                                    setCachedSeries={setCachedSeries}
                                />
                            )
                        )}
                        </div>
                }
                </div>
        )}
        </ContainerDimensions>
        </div>
    )
}

export default ClimavibeEnvChart